import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 180000,
});

// Auto-attach admin token if present
api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("ce_admin_token");
  if (token && cfg.url?.startsWith("/admin")) {
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && err?.config?.url?.startsWith("/admin")) {
      localStorage.removeItem("ce_admin_token");
      if (window.location.pathname.startsWith("/admin") && !window.location.pathname.includes("/login")) {
        window.location.href = "/admin/login";
      }
    }
    return Promise.reject(err);
  }
);

export async function fetchCheckers() {
  const { data } = await api.get("/checkers");
  return data;
}

export async function fetchChecker(slug) {
  const { data } = await api.get(`/checkers/${slug}`);
  return data;
}

export async function submitCheck({ slug, file, extraContext, email, firstName, lastName }) {
  const formData = new FormData();
  formData.append("checker_slug", slug);
  if (extraContext) formData.append("extra_context", extraContext);
  if (file) formData.append("file", file);
  if (email) formData.append("email", email);
  if (firstName) formData.append("first_name", firstName);
  if (lastName) formData.append("last_name", lastName);
  const { data } = await api.post("/checks", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function fetchCheck(id) {
  const { data } = await api.get(`/checks/${id}`);
  return data;
}

export async function postCheckoutQuote({ slug, file, extraContext, email, firstName, lastName, discountCode }) {
  const formData = new FormData();
  formData.append("checker_slug", slug);
  if (extraContext) formData.append("extra_context", extraContext);
  if (email) formData.append("email", email);
  if (firstName) formData.append("first_name", firstName);
  if (lastName) formData.append("last_name", lastName);
  if (discountCode) formData.append("discount_code", discountCode);
  if (file) formData.append("file", file);
  const { data } = await api.post("/checkout/quote", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function fetchCheckStatus(id) {
  const { data } = await api.get(`/checkout/${id}/status`);
  return data;
}

export async function validateDiscount(code) {
  const { data } = await api.post("/discount-codes/validate", { code });
  return data;
}

export async function purchaseBundle(payload) {
  const { data } = await api.post("/credits/bundle/purchase", payload);
  return data;
}

export async function fetchBundleStatus(bundleId) {
  const { data } = await api.get(`/credits/bundle/${bundleId}/status`);
  return data;
}

export async function fetchCredits(email) {
  const { data } = await api.get(`/credits/${encodeURIComponent(email)}`);
  return data;
}

export async function redeemCredit({ email, checkerSlug, extraContext, firstName, lastName, file }) {
  const formData = new FormData();
  formData.append("email", email);
  formData.append("checker_slug", checkerSlug);
  if (extraContext) formData.append("extra_context", extraContext);
  if (firstName) formData.append("first_name", firstName);
  if (lastName) formData.append("last_name", lastName);
  if (file) formData.append("file", file);
  const { data } = await api.post("/credits/redeem-multipart", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function fetchBusiness(slug) {
  const { data } = await api.get(`/businesses/${slug}`);
  return data;
}

// Admin auth extras
export async function requestPasswordReset(email) {
  const { data } = await api.post("/admin/auth/reset-password/request", { email });
  return data;
}

export async function confirmPasswordReset(token, newPassword) {
  const { data } = await api.post("/admin/auth/reset-password/confirm", { token, new_password: newPassword });
  return data;
}

export async function setup2FA() {
  const { data } = await api.post("/admin/auth/2fa/setup");
  return data;
}

export async function verify2FA(code) {
  const { data } = await api.post("/admin/auth/2fa/verify", { code });
  return data;
}

export async function disable2FA() {
  const { data } = await api.post("/admin/auth/2fa/disable");
  return data;
}

// Admin email templates
export async function adminListEmailTemplates() {
  const { data } = await api.get("/admin/email-templates");
  return data;
}

export async function adminUpdateEmailTemplate(key, payload) {
  const { data } = await api.put(`/admin/email-templates/${key}`, payload);
  return data;
}

export async function adminListPayments(params = {}) {
  const { data } = await api.get("/admin/payments", { params });
  return data;
}

export async function adminRefundOrder(orderId, payload) {
  const { data } = await api.post(`/admin/refunds/${encodeURIComponent(orderId)}`, payload);
  return data;
}

export async function adminListRefunds() {
  const { data } = await api.get("/admin/refunds");
  return data;
}

export async function adminMspTestConnection() {
  const { data } = await api.post("/admin/msp/test-connection");
  return data;
}

export async function adminListBundles() {
  const { data } = await api.get("/admin/bundles");
  return data;
}

export async function postContact(payload) {
  const { data } = await api.post("/contact", payload);
  return data;
}

export async function registerBusiness(payload) {
  const { data } = await api.post("/businesses/register", payload);
  return data;
}

export async function fetchBlog() {
  const { data } = await api.get("/blog");
  return data;
}

export async function fetchBlogPost(slug) {
  const { data } = await api.get(`/blog/${slug}`);
  return data;
}

export async function fetchRelatedPosts(slug, limit = 4) {
  const { data } = await api.get(`/blog/${slug}/related`, { params: { limit } });
  return data.posts || [];
}

// Admin
export async function adminLogin(email, password) {
  const { data } = await api.post("/admin/auth/login", { email, password });
  return data;
}

export async function adminMe() {
  const { data } = await api.get("/admin/me");
  return data;
}

export async function adminStats() {
  const { data } = await api.get("/admin/stats");
  return data;
}

export async function adminListChecks(params = {}) {
  const { data } = await api.get("/admin/checks", { params });
  return data;
}

export async function adminDeleteCheck(id) {
  const { data } = await api.delete(`/admin/checks/${id}`);
  return data;
}

export async function adminBulkDelete(ids) {
  const { data } = await api.post("/admin/checks/bulk-delete", { ids });
  return data;
}

export async function adminListBusinesses(status = "") {
  const params = status ? { status } : {};
  const { data } = await api.get("/admin/businesses", { params });
  return data;
}

export async function adminApproveBusiness(id) {
  const { data } = await api.post(`/admin/businesses/${id}/approve`);
  return data;
}

export async function adminRejectBusiness(id) {
  const { data } = await api.post(`/admin/businesses/${id}/reject`);
  return data;
}

export async function adminDeleteBusiness(id) {
  const { data } = await api.delete(`/admin/businesses/${id}`);
  return data;
}

export async function adminGetConfig() {
  const { data } = await api.get("/admin/config");
  return data;
}

export async function adminUpdateConfig(body) {
  const { data } = await api.put("/admin/config", body);
  return data;
}

export async function adminRunGdpr() {
  const { data } = await api.post("/admin/gdpr/run");
  return data;
}

export async function adminListBlog() {
  const { data } = await api.get("/admin/blog-posts");
  return data;
}

export async function adminUpsertBlog(post, slug) {
  if (slug) {
    const { data } = await api.put(`/admin/blog-posts/${slug}`, post);
    return data;
  }
  const { data } = await api.post("/admin/blog-posts", post);
  return data;
}

export async function adminDeleteBlog(slug) {
  const { data } = await api.delete(`/admin/blog-posts/${slug}`);
  return data;
}

export async function adminListDiscountCodes() {
  const { data } = await api.get("/admin/discount-codes");
  return data;
}

export async function adminCreateDiscountCode(payload) {
  const { data } = await api.post("/admin/discount-codes", payload);
  return data;
}

export async function adminDeleteDiscountCode(code) {
  const { data } = await api.delete(`/admin/discount-codes/${encodeURIComponent(code)}`);
  return data;
}
